import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Plus, X, FileText, Store, Eye, MousePointerClick, CreditCard, Wallet, Check, Loader2, Bookmark, Trash2 } from "lucide-react";

const EMPTY = { themes: [], regions: [], education_backgrounds: [], life_situations: [], other_traits: [] };
const CHIP_FIELDS = [
  { key: "themes", ph: "ads.ph_themes" },
  { key: "regions", ph: "ads.ph_regions" },
  { key: "education_backgrounds", ph: "ads.ph_education_backgrounds" },
  { key: "life_situations", ph: "ads.ph_life_situations" },
  { key: "other_traits", ph: "ads.ph_other_traits" },
];

function eur(cents) { return `€${((cents || 0) / 100).toFixed(2)}`; }

export function CampaignWizard({ open, preselect, wallet, onClose }) {
  const t = useT();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [subject, setSubject] = useState(null); // { kind, id, title }
  const [audience, setAudience] = useState(EMPTY);
  const [aiReasoning, setAiReasoning] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [dailyEur, setDailyEur] = useState("10");
  const [durationDays, setDurationDays] = useState("7");
  const [billingMode, setBillingMode] = useState("impression");
  const [paymentSource, setPaymentSource] = useState("stripe");
  const [submitting, setSubmitting] = useState(false);
  const [inputs, setInputs] = useState({});
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplSaving, setTplSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [p, o, tpl] = await Promise.all([
        api.get("/coach/analytics/posts").then((r) => r.data?.rows || []).catch(() => []),
        api.get("/coach/offerings/mine").then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
        api.get("/campaigns/audience-templates").then((r) => r.data?.templates || []).catch(() => []),
      ]);
      setPosts(p.slice(0, 30));
      setOfferings(o);
      setTemplates(tpl);
      if (preselect?.subject_id && preselect?.subject_type) {
        const pool = preselect.subject_type === "post" ? p : o;
        const found = pool.find((x) => x.id === preselect.subject_id);
        if (found) setSubject({ kind: preselect.subject_type, id: found.id, title: found.title || "" });
      }
      setLoading(false);
    })();
  }, [preselect]);

  const totalCents = useMemo(() => {
    const d = Math.max(0, parseInt(dailyEur, 10) || 0);
    const n = Math.max(0, parseInt(durationDays, 10) || 0);
    return d * 100 * n;
  }, [dailyEur, durationDays]);

  const canStep3 = audience.themes.length + audience.regions.length + audience.life_situations.length > 0;
  const canSubmit = canStep3 && !submitting && parseInt(dailyEur, 10) >= 1 && parseInt(durationDays, 10) >= 1 && (paymentSource !== "credits" || wallet >= totalCents);

  const addChip = (field, value) => {
    const v = (value || "").trim();
    if (!v) return;
    setAudience((a) => (a[field].includes(v) ? a : { ...a, [field]: [...a[field], v.slice(0, 60)].slice(0, 8) }));
    setInputs((s) => ({ ...s, [field]: "" }));
  };
  const removeChip = (field, value) => setAudience((a) => ({ ...a, [field]: a[field].filter((x) => x !== value) }));

  const applyTemplate = (tpl) => {
    const a = tpl.audience || {};
    setAudience({
      themes: a.themes || [], regions: a.regions || [], education_backgrounds: a.education_backgrounds || [],
      life_situations: a.life_situations || [], other_traits: a.other_traits || [],
    });
    setAiReasoning("");
    toast.success(t("ads.tpl_applied", { name: tpl.name }));
  };
  const saveTemplate = async () => {
    const name = tplName.trim();
    if (!name) return;
    setTplSaving(true);
    try {
      const { data } = await api.post("/campaigns/audience-templates", { name, audience });
      setTemplates((prev) => [data, ...prev]);
      setTplName("");
      toast.success(t("ads.tpl_saved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setTplSaving(false); }
  };
  const deleteTemplate = async (id) => {
    try { await api.delete(`/campaigns/audience-templates/${id}`); setTemplates((prev) => prev.filter((x) => x.id !== id)); toast.success(t("ads.tpl_deleted")); }
    catch (e) { toast.error(apiError(e)); }
  };

  const aiSuggest = useCallback(async () => {
    if (!subject) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/campaigns/ai-audience", { subject_type: subject.kind, subject_id: subject.id });
      setAudience({
        themes: data.themes || [], regions: data.regions || [], education_backgrounds: data.education_backgrounds || [],
        life_situations: data.life_situations || [], other_traits: data.other_traits || [],
      });
      setAiReasoning(data.reasoning || "");
      toast.success(t("ads.ai_ok"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiBusy(false); }
  }, [subject, t]);

  const submit = async () => {
    if (!subject) return;
    setSubmitting(true);
    try {
      await api.post("/campaigns", {
        subject_type: subject.kind,
        subject_id: subject.id,
        audience,
        budget: { daily_cents: Math.max(100, parseInt(dailyEur, 10) * 100), duration_days: Math.max(1, parseInt(durationDays, 10)) },
        billing_mode: billingMode,
        payment_source: paymentSource,
      });
      toast.success(t("ads.new_ok"));
      onClose(true);
    } catch (e) {
      const msg = apiError(e);
      toast.error(msg.includes("insufficient_credits") ? t("ads.no_credits") : msg);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="ads-wizard">
        <DialogHeader>
          <DialogTitle>{t("ads.new_title")}</DialogTitle>
          <DialogDescription>{t("ads.new_step", { n: step })}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5">
          {[1, 2, 3].map((n) => <div key={n} className={`h-1 flex-1 rounded ${step >= n ? "bg-primary" : "bg-border"}`} />)}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="mt-2 space-y-4">
            {/* Step 1 — subject */}
            {step === 1 && (
              <>
                <p className="text-sm font-semibold text-foreground">{t("ads.pick_subject")}</p>
                <p className="text-xs text-muted-foreground">{t("ads.pick_subject_hint")}</p>
                {offerings.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.subject_offerings")}</p>
                    <div className="space-y-1.5">
                      {offerings.slice(0, 8).map((o) => (
                        <SubjectRow key={o.id} icon={Store} title={o.title} on={subject?.kind === "offering" && subject.id === o.id} onClick={() => setSubject({ kind: "offering", id: o.id, title: o.title || "" })} testId={`ads-subj-offering-${o.id}`} />
                      ))}
                    </div>
                  </div>
                )}
                {posts.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.subject_posts")}</p>
                    <div className="space-y-1.5">
                      {posts.slice(0, 10).map((p) => (
                        <SubjectRow key={p.id} icon={FileText} title={p.title} on={subject?.kind === "post" && subject.id === p.id} onClick={() => setSubject({ kind: "post", id: p.id, title: p.title || "" })} testId={`ads-subj-post-${p.id}`} />
                      ))}
                    </div>
                  </div>
                )}
                {offerings.length === 0 && posts.length === 0 && (
                  <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">{t("ads.no_subjects")}</p>
                )}
              </>
            )}

            {/* Step 2 — audience */}
            {step === 2 && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{t("ads.audience")}</p>
                  <Button size="sm" className="gap-1.5 rounded-full" onClick={aiSuggest} disabled={aiBusy} data-testid="ads-ai-suggest">
                    {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {t("ads.ai_button")}
                  </Button>
                </div>

                {/* Reusable audience templates */}
                <div className="rounded-xl border border-border bg-secondary/40 p-3" data-testid="ads-tpl-box">
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Bookmark className="h-3.5 w-3.5 text-primary" /> {t("ads.tpl_label")}</p>
                  {templates.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">{t("ads.tpl_none")}</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {templates.map((tpl) => (
                        <span key={tpl.id} className="inline-flex items-center gap-1 rounded-full bg-primary/15 py-1 pl-3 pr-1 text-xs text-primary" data-testid={`ads-tpl-${tpl.id}`}>
                          <button onClick={() => applyTemplate(tpl)} className="font-medium hover:underline" data-testid={`ads-tpl-apply-${tpl.id}`}>{tpl.name}</button>
                          <button onClick={() => deleteTemplate(tpl.id)} className="rounded-full p-0.5 hover:bg-primary/20" data-testid={`ads-tpl-del-${tpl.id}`}><Trash2 className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <Input value={tplName} onChange={(e) => setTplName(e.target.value)} maxLength={80} placeholder={t("ads.tpl_name_ph")} className="h-8 flex-1 text-sm" data-testid="ads-tpl-name" />
                    <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1.5 rounded-full" disabled={tplSaving || !tplName.trim() || !canStep3} onClick={saveTemplate} data-testid="ads-tpl-save">
                      {tplSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bookmark className="h-3.5 w-3.5" />} {t("ads.tpl_save")}
                    </Button>
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">{t("ads.tpl_save_hint")}</p>
                </div>

                {aiReasoning && (
                  <div className="rounded-xl bg-primary/5 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("ads.ai_why")}</p>
                    <p className="mt-0.5 text-sm text-foreground">{aiReasoning}</p>
                  </div>
                )}
                {CHIP_FIELDS.map((f) => (
                  <ChipField
                    key={f.key}
                    label={t(`ads.field_${f.key}`)}
                    placeholder={t(f.ph)}
                    items={audience[f.key]}
                    value={inputs[f.key] || ""}
                    onChange={(v) => setInputs((s) => ({ ...s, [f.key]: v }))}
                    onAdd={() => addChip(f.key, inputs[f.key])}
                    onRemove={(v) => removeChip(f.key, v)}
                    testId={`ads-chip-${f.key}`}
                  />
                ))}
              </>
            )}

            {/* Step 3 — budget + payment */}
            {step === 3 && (
              <>
                <p className="text-sm font-semibold text-foreground">{t("ads.budget")}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.daily_eur")}</label>
                    <Input type="number" min="1" value={dailyEur} onChange={(e) => setDailyEur(e.target.value)} data-testid="ads-daily" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.duration_days")}</label>
                    <Input type="number" min="1" max="90" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} data-testid="ads-duration" />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <span className="text-sm text-muted-foreground">{t("ads.total")}</span>
                  <span className="font-display text-xl font-semibold text-primary">€{(totalCents / 100).toFixed(0)}</span>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.billing_mode")}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ k: "impression", icon: Eye }, { k: "click", icon: MousePointerClick }].map(({ k, icon: Icon }) => (
                      <button key={k} onClick={() => setBillingMode(k)} data-testid={`ads-billing-${k}`} className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-medium ${billingMode === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                        <Icon className="h-4 w-4" /> {t(`ads.billing_${k}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.payment")}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setPaymentSource("stripe")} data-testid="ads-pay-stripe" className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-medium ${paymentSource === "stripe" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                      <CreditCard className="h-4 w-4" /> {t("ads.pay_stripe")}
                    </button>
                    <button onClick={() => setPaymentSource("credits")} data-testid="ads-pay-credits" className={`flex flex-col items-center justify-center gap-0.5 rounded-xl border p-3 text-sm font-medium ${paymentSource === "credits" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                      <span className="flex items-center gap-2"><Wallet className="h-4 w-4" /> {t("ads.pay_credits")}</span>
                      <span className="text-xs opacity-70">{eur(wallet)}</span>
                    </button>
                  </div>
                  {paymentSource === "credits" && wallet < totalCents && (
                    <p className="mt-1.5 text-xs text-rose-600">{t("ads.credits_short")}</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer nav */}
        {!loading && (
          <div className="mt-2 flex justify-between gap-2 border-t border-border pt-3">
            <Button variant="outline" className="rounded-full" onClick={() => (step > 1 ? setStep(step - 1) : onClose(false))}>
              {step > 1 ? t("ads.back_step") : t("common.cancel")}
            </Button>
            {step < 3 ? (
              <Button className="rounded-full" disabled={(step === 1 && !subject) || (step === 2 && !canStep3)} onClick={() => setStep(step + 1)} data-testid="ads-wizard-next">{t("ads.next")}</Button>
            ) : (
              <Button className="gap-1.5 rounded-full" disabled={!canSubmit} onClick={submit} data-testid="ads-wizard-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("ads.launch")}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubjectRow({ icon: Icon, title, on, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`flex w-full items-center gap-2 rounded-xl border p-2.5 text-left ${on ? "border-primary bg-primary/10" : "border-border hover:bg-accent/40"}`}>
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{title || "—"}</span>
      {on && <Check className="h-4 w-4 shrink-0 text-primary" />}
    </button>
  );
}

function ChipField({ label, placeholder, items, value, onChange, onAdd, onRemove, testId }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      <div className="flex gap-2">
        <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} className="h-9" data-testid={`${testId}-input`} />
        <Button type="button" size="icon" variant="outline" className="h-9 w-9 shrink-0 rounded-full" onClick={onAdd} data-testid={`${testId}-add`}><Plus className="h-4 w-4" /></Button>
      </div>
      {items.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {items.map((it) => (
            <span key={it} className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs text-primary" data-testid={`${testId}-chip-${it}`}>
              {it}
              <button onClick={() => onRemove(it)} className="hover:text-primary/70"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
